/**
 * LoginForm component
 *
 * @author Ritesh Shrivastav
 * @author Akhila
*/
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Footer from '../Footer';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {FormGroup, Box, Typography} from '@material-ui/core';
import GoogleSignInImage from '../../assets/google/btn_google_signin_dark_normal_web@2x.png';
import GoogleSignInImageDisabled from '../../assets/google/btn_google_signin_dark_disabled_web@2x.png';
import MicrosoftSignInImage from '../../assets/microsoft/ms-symbollockup_signin_light.png';
import MicrosoftSignInDisabled from '../../assets/microsoft/ms-symbollockup_signin_dark.png';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import Loader from '../Loader';
import logo from '../../assets/logo-blue.svg';
import PropTypes from 'prop-types';
import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import withActionTracker from '../Track/hoc/withActionTracker';
import { encodeNext, decodeNext } from '../../helpers/string/nextPath';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { OAUTH2_GOOGLE_STATE_NEXT_PREFIX } from '../../constants/View';
import { IconButton, InputAdornment } from '@material-ui/core';
import { VisibilityOutlined as Visibility, VisibilityOffOutlined as VisibilityOff } from '@material-ui/icons';


const styles = theme => ({
  root: {
    alignItems: 'left',
    margin: theme.spacing(3),
  },
  loginSubDivision: {
    lineHeight: '40px',
    paddingTop: '40px'
  },
  loginHeader: {
    display:'block',
    margin: '0.5rem',
    paddingBottom:'0.5rem',
    fontWeight:'600',
    fontSize:'28px',
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  formControl: {
    margin: theme.spacing(1),
  },
  googleSigninContainer: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  footer: {
    marginTop: '2em',
  },
  footerLink: {
    textDecoration: 'none',
    marginRight: '1em'
  },
  googleSignInBtn: {
    left: '-3px',
  },
  microsoftSignInBtn:{
    left: '-3px',
    marginLeft: theme.spacing(1)
  },
  dividerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  divider: {
    width: '49%',
    display: 'inline-block'
  },
  dividerOr: {
    display: 'inline-block',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
});

class LoginForm extends React.PureComponent {
    constructor(props) {
    super(props);
    this.emailRef = React.createRef();
    this.passwordRef = React.createRef();
    this.handleEmailKeyPress = this.handleEmailKeyPress.bind(this);
  }

  static propTypes = {
    classes: PropTypes.object.isRequired,
    fetchGoogleSettingsForLogin: PropTypes.func.isRequired,
    googleSettingsForLoginLoading: PropTypes.bool.isRequired,
    googleSettingsForLoginError: PropTypes.string,

    fetchMicrosoftSettingsForLogin: PropTypes.func.isRequired,
    microsoftSettingsForLoginLoading: PropTypes.bool.isRequired,
    microsoftSettingsForLoginError: PropTypes.string,

    location: PropTypes.object.isRequired,
    login: PropTypes.func.isRequired,
    loginLoading: PropTypes.bool.isRequired,
    loginError: PropTypes.string,
    /**
     * Function to track action
     */
    trackAction: PropTypes.func.isRequired,
  };

  state = {
    email: '',
    password: '',
    rememberMe: false,
    googleAuthURL: '',
    microsoftAuthURL: '',
    showPassword: false
  };

  handleSubmit = e => {
    e && e.preventDefault();
    if (this.props.loginLoading) return;
    this.props.trackAction('clicked login');
    const params = new URLSearchParams(this.props.location.search);
    const next = (params.get('next') ? decodeNext(params.get('next')) : '/dashboard');
    this.props.login(this.state, next);
  };

  handleChange = name => event => {
    if (event.target.type !== 'checkbox') {
      this.setState({
        [name]: event.target.value
      });
    } else {
      this.setState({
        [name]: event.target.checked
      });
    }
  };

  handleClickShowPassword = () => {
    this.setState({showPassword: !this.state.showPassword})
  }

  componentDidMount() {
    const { fetchGoogleSettingsForLogin,fetchMicrosoftSettingsForLogin, location } = this.props;
    const params = new URLSearchParams(location.search);
    const next = (params.get('next') ? params.get('next') : encodeNext('/dashboard'));
    // Fetch settings on component mount
    //For google
    fetchGoogleSettingsForLogin()
    .then(({ data }) => {
      if (data && data.google_auth_url) {
        // Insert next into state param of the auth url, which will be returned
        // to us when google redirects post-user consent
        const google_auth_url = data.google_auth_url;
        const googleAuthURL = new URL(google_auth_url);
        // update state, add the encoded next path in the state that we'll read
        // from in the redirect handler
        const state_with_next = ((googleAuthURL.searchParams.get('state') || '') +
          `-${OAUTH2_GOOGLE_STATE_NEXT_PREFIX}${next}`);
        googleAuthURL.searchParams.set('state', state_with_next);

        this.setState({
          googleAuthURL: googleAuthURL.toString()
        });
      }
    })
    .catch(()=>{});

    //For microsoft
    fetchMicrosoftSettingsForLogin()
    .then(({data}) =>{
      if(data && data.microsoft_auth_url){
        // Insert next into state param of the auth url, which will be returned
        // to us when microsoft redirects post-user consent
        const {microsoft_auth_url} = data;
        const microsoftAuthURL = new URL(microsoft_auth_url);
        // update state, add the encoded next path in the state that we'll read
        // from in the redirect handler
        const state_with_next = ((microsoftAuthURL.searchParams.get('state') || '') +
        `-${OAUTH2_GOOGLE_STATE_NEXT_PREFIX}${next}`);
        microsoftAuthURL.searchParams.set('state', state_with_next);

        this.setState({
          microsoftAuthURL: microsoftAuthURL.toString()
        });
      }
    })
    .catch(() => {});
  }
    handleEmailKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.emailRef.current.blur();
      this.passwordRef.current.focus();
    }
  }

  render() {
    const { loginLoading, classes, trackAction } = this.props;
    const { 
      googleSettingsForLoginLoading, 
      googleSettingsForLoginError,
      microsoftSettingsForLoginLoading,
      microsoftSettingsForLoginError, 
    } = this.props;

    const { googleAuthURL,microsoftAuthURL } = this.state;
    const googleLoginDisabled = (loginLoading || googleSettingsForLoginLoading ||
      !!googleSettingsForLoginError || !!!googleAuthURL);
    const microsoftLoginDisabled = (loginLoading || microsoftSettingsForLoginLoading ||
      !!microsoftSettingsForLoginError || !!!microsoftAuthURL);
    return (
      <div className={classes.root}>
        <Link to="/">
          <img src={logo} alt="Taghash" />
        </Link>
        <div className={classes.loginSubDivision}>
          <Typography  className={classes.loginHeader}>Sign In</Typography>
          <form className={classes.form} onSubmit={this.handleSubmit}>
            <FormControl
              className={classes.formControl}
              fullWidth
              required
              disabled={loginLoading}
            
            >
              <InputLabel>Email address</InputLabel>
              <Input
                autoComplete='username'
                  inputRef= {this.emailRef}
                value={this.state.email}
                onChange={this.handleChange('email')}
  
                onKeyPress={this.handleEmailKeyPress}
              />
            </FormControl>
            <FormControl
              className={classes.formControl}
              fullWidth
              required
              disabled={loginLoading}
            
            >
              <InputLabel>Password</InputLabel>
              <Input
                id="standard-adornment-password"
                autoComplete='current-password'
                type={this.state.showPassword ? 'text' : 'password'}
                value={this.state.password}
                    inputRef={this.passwordRef}
                onChange={this.handleChange('password')}
                
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={this.handleClickShowPassword}
                    >
                      {this.state.showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <FormControl className={classes.formControl} fullWidth disabled={loginLoading}>
              <FormControlLabel
                label="Remember me"
                control={
                  <Checkbox
                    type='checkbox'
                    checked={this.state.rememberMe}
                    onChange={this.handleChange('rememberMe')}
                  />
                }
              />
            </FormControl>
            <FormGroup row>
          <Box display="flex" alignItems="center" gap={2}>
          <FormControl className={classes.formControl} disabled={loginLoading}>
            <Button
              type="submit"
              size="medium"
              variant="contained"
              color="primary"
              fullWidth
              onClick={this.handleSubmit}
              style={{fontWeight:500}}
            >
              Login
            </Button>
          </FormControl>
          <FormControl className={classes.formControl} disabled={loginLoading}>
            <Button
              component={Link}
              to="/reset-password"
              size="medium"
              variant="text"
              sx={{ minWidth: 'auto', padding:  '0.5rem 1rem', fontSize: '0.875rem' }}
            >
                  Reset Password
            </Button>
          </FormControl>
        </Box>
        </FormGroup>
          </form>
          <div className={classes.dividerContainer}>
            <Divider className={classes.divider} />
            <div className={classes.dividerOr}>or</div>
            <Divider className={classes.divider} />
          </div>
          <div className={classes.googleSigninContainer}>
            <ButtonBase
              type="button"
              className={classes.googleSignInBtn}
              component="a"
              href={googleAuthURL}
              disabled={googleLoginDisabled}
              onClick={() => {
                // do not prevent default
                trackAction('clicked sign in with google');
              }}
            >
              <img
                src={googleLoginDisabled ? GoogleSignInImageDisabled : GoogleSignInImage}
                alt="Sign in with Google" 
                height="44px"
              />
            </ButtonBase>
            <ButtonBase
                type="button"
                className={classes.microsoftSignInBtn}
                component="a"
                href={microsoftAuthURL}
                disabled={microsoftLoginDisabled}
                onClick={() => {
                  trackAction('Clicked sign in with microsoft')
                }}
              >
                <img 
                  src={microsoftLoginDisabled? MicrosoftSignInDisabled: MicrosoftSignInImage}
                  alt="Sign in with Microsoft"
                  height="38px"
                  width="184px"
                />
              </ButtonBase>
          </div>
          <Loader active={loginLoading} />
        </div>
        <Footer />
      </div>
    );
  }
}

export default withActionTracker(
  withStyles(styles)(LoginForm),
  'LoginForm'
);
